import { useEffect, useRef, useState } from 'react';
import {
  TerraDraw,
  TerraDrawLineStringMode,
  TerraDrawPointMode,
  TerraDrawPolygonMode,
} from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';

export type DrawMode = 'point' | 'linestring' | 'polygon';

function createModes() {
  return [
    new TerraDrawPointMode({ editable: true }),
    new TerraDrawLineStringMode({ editable: true }),
    new TerraDrawPolygonMode({ editable: true }),
  ];
}

/**
 * Drives a TerraDraw instance over a `@react-google-maps/api` map. Pass the raw
 * `google.maps.Map` instance (from `<GoogleMap onLoad>`); returns the TerraDraw
 * instance once the map projection is ready, or null until then.
 *
 * Replaces the old `<DrawingManager>`, which crashed after Google removed the
 * `drawing` library from the Maps JS API.
 */
export function useTerraDraw(map: google.maps.Map | null) {
  const drawRef = useRef<TerraDraw | null>(null);
  const [draw, setDraw] = useState<TerraDraw | null>(null);

  useEffect(() => {
    if (!map || drawRef.current) return;

    let isCancelled = false;
    let listener: google.maps.MapsEventListener | null = null;

    const initialize = () => {
      if (drawRef.current || isCancelled) return;

      const instance = new TerraDraw({
        adapter: new TerraDrawGoogleMapsAdapter({
          map,
          lib: google.maps,
          coordinatePrecision: 9,
        }),
        modes: createModes(),
      });

      instance.start();
      drawRef.current = instance;
      setDraw(instance);
    };

    if (map.getProjection()) {
      initialize();
    } else {
      listener = map.addListener('projection_changed', () => {
        if (!map.getProjection()) return;
        listener?.remove();
        initialize();
      });
    }

    return () => {
      isCancelled = true;
      listener?.remove();
      if (drawRef.current) {
        drawRef.current.stop();
        drawRef.current = null;
      }
      setDraw(null);
    };
  }, [map]);

  return draw;
}
